import React from 'react';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { usePermissionGuard } from '../../utils/permissionGuard';

/**
 * PermissionGuard - Protects routes based on RBAC permissions
 * Redirects to 403 page or shows error if user lacks required permissions
 */
function PermissionGuard({ 
  children, 
  resource, 
  action, 
  organizationWide = false,
  fallback = null 
}) {
  const { user } = useUserPermissions();
  const permissions = usePermissionGuard(user);

  // Loading state
  if (!user) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-slate-600">Checking permissions...</div>
        </div>
      </div>
    );
  }

  // Check permissions
  let hasPermission = false;
  
  if (organizationWide) {
    hasPermission = permissions.hasOrganizationAccess(resource, action);
  } else {
    hasPermission = permissions.hasPermission(resource, action);
  }

  // If no permission, show access denied
  if (!hasPermission) {
    if (fallback) {
      return fallback;
    }

    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
          <div className="mb-4">
            <svg className="w-16 h-16 text-red-600 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                d="M12 9v3.75m0-10.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.75c0 5.592 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.57-.598-3.75A11.96 11.96 0 0012 2.25z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15h.01" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this page.
          </p>
          <div className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 mb-4">
            <div><strong>Required:</strong> {organizationWide ? 'Organization-wide' : 'Standard'} {action} access to {resource}</div>
            <div><strong>Your Role:</strong> {user?.role || 'Unknown'}</div>
          </div>
          <div className="space-y-2">
            <button 
              onClick={() => window.history.back()} 
              className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Go Back
            </button>
            <div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If permission granted, render children
  return children;
}

export default PermissionGuard;